import { useEffect, useState } from "react";
import {
  Cloud, Rocket, ExternalLink, Copy, Trash2, Loader2, CheckCircle2, XCircle,
  Globe, Server, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

function CrStatus({ cr }) {
  if (!cr) return <span className="font-mono text-xs text-zinc-600">not deployed</span>;
  const map = {
    BUILDING: { icon: Loader2, cls: "text-amber-400", spin: true, label: "Building" },
    READY: { icon: CheckCircle2, cls: "text-emerald-400", label: "Live" },
    FAILED: { icon: XCircle, cls: "text-rose-400", label: "Failed" },
  };
  const m = map[cr.status] || map.FAILED;
  return (
    <span className={cn("inline-flex items-center gap-1.5 font-mono text-xs", m.cls)}>
      <m.icon className={cn("h-3.5 w-3.5", m.spin && "animate-spin")} /> {m.label}
    </span>
  );
}

export default function CloudRun() {
  const [status, setStatus] = useState(null);
  const [sites, setSites] = useState(null);
  const [busy, setBusy] = useState(null);
  const { canWrite } = useAuth();

  const loadSites = () => api.get("/sites").then(setSites).catch(() => setSites([]));
  useEffect(() => {
    api.get("/cloudrun/status").then(setStatus).catch(() => setStatus({ configured: false }));
    loadSites();
  }, []);

  // poll while anything is building
  useEffect(() => {
    if (!sites) return;
    const building = sites.some((s) => s.cloudrun?.status === "BUILDING");
    if (!building) return;
    const t = setInterval(loadSites, 5000);
    return () => clearInterval(t);
  }, [sites]);

  const deploy = async (slug) => {
    setBusy(slug);
    try {
      await api.post(`/cloudrun/deploy/${slug}`);
      toast.success("Build started — this takes ~1–3 min");
      loadSites();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deploy failed"); }
    finally { setBusy(null); }
  };
  const remove = async (slug) => {
    try { await api.post(`/cloudrun/delete/${slug}`); toast.success("Service deleted"); loadSites(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("URL copied"); };

  if (!sites || !status) return <Loading label="Loading Cloud Run" />;

  return (
    <div data-testid="cloudrun-page">
      <PageHeader
        title="Cloud Run"
        breadcrumb="cannon / cloud run"
        subtitle="Deploy any site to Google Cloud Run and get a public HTTPS link"
        actions={
          <span className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-mono",
            status.configured ? "border-emerald-500/20 text-emerald-400 bg-emerald-500/5" : "border-rose-500/20 text-rose-400 bg-rose-500/5")}>
            <Cloud className="h-3.5 w-3.5" /> {status.configured ? `${status.project} · ${status.region}` : "Not configured"}
          </span>
        }
      />

      {!status.configured && (
        <div className="mb-4 rounded-md border border-amber-500/20 bg-amber-500/5 text-amber-300 px-4 py-3 text-sm font-mono">
          Cloud Run isn't configured on the server (missing GCP credentials).
        </div>
      )}

      {sites.length === 0 ? (
        <EmptyState icon={Cloud} title="No sites to deploy">Create a site first, then deploy it here.</EmptyState>
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-2.5">Site</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Cloud Run</th>
                <th className="px-4 py-2.5">Public URL</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => {
                const cr = s.cloudrun;
                const building = cr?.status === "BUILDING";
                return (
                  <tr key={s.slug} className="border-b border-zinc-800/70 hover:bg-zinc-900/40" data-testid={`cr-row-${s.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {s.type === "node" ? <Server className="h-4 w-4 text-violet-400" /> : <Globe className="h-4 w-4 text-sky-400" />}
                        <div><div className="text-zinc-100">{s.name}</div><div className="font-mono text-xs text-zinc-500">{s.slug}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={s.type} /></td>
                    <td className="px-4 py-3"><CrStatus cr={cr} /></td>
                    <td className="px-4 py-3">
                      {cr?.url ? (
                        <div className="flex items-center gap-1.5">
                          <a href={cr.url} target="_blank" rel="noopener noreferrer" data-testid={`cr-url-${s.slug}`}
                            className="font-mono text-xs text-emerald-400 hover:underline truncate max-w-[240px]">{cr.url.replace("https://", "")}</a>
                          <button onClick={() => copy(cr.url)} className="text-zinc-500 hover:text-zinc-200"><Copy className="h-3.5 w-3.5" /></button>
                          <a href={cr.url} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-zinc-200"><ExternalLink className="h-3.5 w-3.5" /></a>
                        </div>
                      ) : <span className="font-mono text-xs text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canWrite && status.configured && (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => deploy(s.slug)} disabled={busy === s.slug || building} data-testid={`cr-deploy-${s.slug}`}
                            className="inline-flex items-center gap-1 rounded-md bg-zinc-100 text-zinc-900 px-2.5 py-1.5 text-xs font-medium hover:bg-white disabled:opacity-50">
                            {building || busy === s.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : cr?.url ? <RefreshCw className="h-3.5 w-3.5" /> : <Rocket className="h-3.5 w-3.5" />}
                            {cr?.url ? "Redeploy" : "Deploy"}
                          </button>
                          {cr && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button data-testid={`cr-delete-${s.slug}`} className="p-1.5 text-zinc-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
                                <AlertDialogHeader><AlertDialogTitle>Delete Cloud Run service?</AlertDialogTitle>
                                  <AlertDialogDescription>This removes the deployed service <span className="font-mono">{cr.service}</span> and its public URL.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => remove(s.slug)} className="bg-rose-600 hover:bg-rose-500">Delete</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sites.some((s) => s.cloudrun?.status === "FAILED") && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Last build log (failed)</div>
          {sites.filter((s) => s.cloudrun?.status === "FAILED").map((s) => (
            <pre key={s.slug} className="mb-2 max-h-48 overflow-auto rounded-md border border-rose-500/20 bg-black p-3 text-xs font-mono text-rose-300 whitespace-pre-wrap">
              [{s.slug}] {s.cloudrun.log}
            </pre>
          ))}
        </div>
      )}
    </div>
  );
}
